import { actions, CurrentUserWithAdminFlags } from './reducer'
import ssapi from 'lib/api/secondstepApi'
import { addNotification } from 'store/notifications'
import { getReadableErrorMessage } from 'utils/errorUtils'
import { UpdateUserProfileRequest, GroupMembership } from 'types'

/** Whitelist fields for SSAPI PUT /profile and PUT /profile/me. */
export function updateProfileRequest(
  profile: UpdateUserProfileRequest & Record<string, unknown>,
): UpdateUserProfileRequest {
  const netsuiteCustomerId =
    profile.netsuiteCustomerId ??
    (profile.netSuiteCustomerId as number | undefined) ??
    0

  return {
    id: profile.id,
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    email: profile.email ?? '',
    title: profile.title ?? '',
    siteName: profile.siteName ?? '',
    netsuiteCustomerId,
    country: profile.country ?? '',
    region: profile.region ?? '',
    city: profile.city ?? '',
    archetypes: profile.archetypes ?? [],
  }
}

export const fetchCurrentUser = () => {
  return async function (dispatch) {
    dispatch(actions.fetchingCurrentUser())

    try {
      const { data } = await ssapi.getUserProfile()
      const groups = data.groupMembership || []

      const currentUser: CurrentUserWithAdminFlags = {
        ...data,
        isCssAdmin: groups.includes(GroupMembership.CSS_ADMINS),
        isTechnologyAdmin: groups.includes(GroupMembership.TECHNOLOGY_ADMINS),
        isOperationsAdmin: groups.includes(GroupMembership.OPERATIONS_ADMINS),
        isAdmin: Object.values(GroupMembership).some(group =>
          groups.includes(group),
        ),
      }

      dispatch(actions.fetchCurrentUserSuccess(currentUser))

      return currentUser
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Error fetching current user',
      )
      dispatch(actions.fetchCurrentUserError(errorMessage))
      return false
    }
  }
}

/**
 * Updates the current user's profile and refreshes the current user data
 */
export const updateUserProfile = (profileData: UpdateUserProfileRequest) => {
  return async function (dispatch) {
    dispatch(actions.updatingUserProfile())

    try {
      await ssapi.updateUserProfile(updateProfileRequest(profileData))
      dispatch(
        addNotification({
          color: 'success',
          text: 'Successfully updated profile',
        }),
      )
      await dispatch(fetchCurrentUser())
      dispatch(actions.updateUserProfileSuccess())
    } catch (error) {
      const errorMessage = getReadableErrorMessage(
        error,
        'Failed to update profile',
      )
      dispatch(
        addNotification({
          color: 'danger',
          text: errorMessage,
        }),
      )
      dispatch(actions.updateUserProfileError(errorMessage))
    }
  }
}

/**
 * Clears the update status flags
 */
export const clearUpdateStatus = () => {
  return function (dispatch) {
    dispatch(actions.clearUpdateStatus())
  }
}

/**
 * Clears the current user from state
 */
export const clearCurrentUser = () => {
  return function (dispatch) {
    dispatch(actions.clearCurrentUser())
  }
}
